import { useContext, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Lottie from 'lottie-react'
import LoaderNewUser from '../../../../../assets/svgIcons/loaderNewUser.json'
import { BusinessAPI } from '../../../../../api/business.api'
import GlobalContext from '../../../../../context/global-context'
import { DashboardAPI } from '../../../../../api/dashboard.api'

export function StepForm5() {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentBusiness } = useContext(GlobalContext)

  // Si venimos de AddNewCompany, se envió newBusinessId en location.state
  const businessId = location.state?.newBusinessId ?? currentBusiness?.id

  const [status, setStatus] = useState('pending') // 'pending' | 'loaded' | 'error'
  const [callCount, setCallCount] = useState(0)
  const [, setMinutesUntilNext] = useState(1) // <- arreglado: tomamos SOLO el setter

  const messages = [
    'Conectando con el Servicio de Impuestos Internos… estamos ingresando tus credenciales de forma segura.',
    'Extrayendo tus movimientos de ventas, compras y honorarios… LUCA ya está trayendo la información oficial de tu empresa.',
    'Organizando todo en nuestra base de datos inteligente… aquí no se pierde ni un solo detalle.',
    'Aplicando algoritmos contables a tus documentos… LUCA ya está clasificando facturas, boletas y gastos automáticamente.',
    'Identificando patrones para facilitar tu gestión… tu información empieza a cobrar sentido.',
    'Preparando reportes e indicadores claves para ti… porque mereces decisiones inteligentes, no solo datos.',
    '¡Todo listo en unos segundos más!.. LUCA está haciendo magia con tus datos.'
  ]

  useEffect(() => {
    if (!businessId) return

    // Determinar mes/año a consultar:
    const now = new Date()
    let yearToFetch = now.getFullYear()
    let monthToFetch = now.getMonth() + 1 // 1–12

    // Si estamos en los primeros 5 días, pasamos al mes anterior
    if (now.getDate() <= 5) {
      monthToFetch -= 1
      if (monthToFetch < 1) {
        monthToFetch = 12
        yearToFetch -= 1
      }
    }

    const api = new BusinessAPI()
    const dashboardApi = new DashboardAPI()
    let pollingInterval

    const checkMovements = async () => {
      try {
        const [ing, eg] = await Promise.all([
          api
            .getListSummaryMovements({
              type: 'ingreso',
              businessId,
              month: monthToFetch,
              year: yearToFetch,
              linkage: 1
            })
            .then(r => r.data ?? []),
          api
            .getListSummaryMovements({
              type: 'egreso',
              businessId,
              month: monthToFetch,
              year: yearToFetch,
              linkage: 1
            })
            .then(r => r.data ?? [])
        ])

        if (ing.length > 0 || eg.length > 0) {
          return 'loaded'
        }
      } catch (err) {
        return 'error'
      }
    }

    const checkDashboards = async () => {
      try {
        const [businessInsightsDashboardData, administrationDashboardData] = await Promise.all([
          api.getLatestVisionDelNegocio({ businessId }).then(r => r.data),
          dashboardApi.getAdministrationDashboard(businessId, { year: yearToFetch }).then(r => r.data)
        ])

        if (Object.values(businessInsightsDashboardData).some(value => value === null)) {
          api.getVisionDelNegocio({ businessId }).then(r => r.data)
          throw new Error('Información incompleta de Business Insights')
        }

        if (
          [null, undefined].includes(administrationDashboardData.amountToPay) &&
          [null, undefined].includes(administrationDashboardData.amountToReceive)
        ) {
          throw new Error('No se encontraron datos de administración')
        }

        if (
          [null, undefined].includes(businessInsightsDashboardData.erGastosMesActual) &&
          [null, undefined].includes(businessInsightsDashboardData.erGastosAnual)
        ) {
          throw new Error('No se encontraron datos de business insights')
        }
        return 'loaded'
      } catch (error) {
        return 'retry'
      }
    }

    const doCheck = async () => {
      setCallCount(c => c + 1)
      const movementsResult = await checkMovements()
      if (movementsResult === 'loaded') {
        const dashboardsResult = await checkDashboards()
        if (dashboardsResult === 'loaded') {
          setStatus('loaded')
          clearInterval(pollingInterval)
        }
        
        return
      } else if (movementsResult === 'error') {
        setStatus('error')
        clearInterval(pollingInterval)
      }
    }

    // Primera llamada inmediata
    doCheck()

    // Polling cada minuto (y actualiza countdown aquí mismo)
    pollingInterval = setInterval(() => {
      doCheck()
      setMinutesUntilNext(prev => (prev > 1 ? prev - 1 : 1))
    }, 60000)

    return () => {
      clearInterval(pollingInterval)
    }
  }, [businessId])

  useEffect(() => {
    if (status === 'loaded') {
      const t = setTimeout(() => navigate('/', { replace: true }), 2000)
      return () => clearTimeout(t)
    }
  }, [status, navigate])

  const currentMessage =
    status === 'pending'
      ? messages[Math.min(callCount, messages.length - 1)]
      : status === 'loaded'
        ? messages[messages.length - 1]
        : null
  const lottieStyle = { width: 'min(90vw, 700px)', height: 'min(64vw, 500px)' }

  return (
    <div className='flex items-center justify-center min-h-screen p-4 bg-gray-50'>
      <div className='w-full p-6'>
        <h2 className='text-xl font-semibold text-center mb-2'>
          Estamos procesando la información de tu empresa
        </h2>
        <p className='text-sm text-gray-500 text-center mb-6'>
          Este proceso puede tomar máximo 30 minutos. Si quieres, puedes ir por un café mientras LUCA
          organiza todo por ti.
        </p>

        <div className='flex flex-col items-center gap-4'>
          {status === 'pending' && (
            <>
              <Lottie animationData={LoaderNewUser} loop style={lottieStyle} />
              <p className='text-gray-600 text-center'>{currentMessage}</p>
            </>
          )}

          {status === 'loaded' && (
            <>
              <Lottie animationData={LoaderNewUser} loop style={lottieStyle} />
              <p className='text-green-600 font-medium text-center'>{currentMessage}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <p className='text-red-600 text-center mb-4'>Error al conectar con el SII.</p>
              <button onClick={() => window.location.reload()} className='btn-primary'>
                Reintentar
              </button>
            </>
          )}
          <br />
          <p className='text-center'>
            En caso de problemas, contactate con el equipo a{' '}
            <span className='text-accent1'>soporte@tryluca.cl</span>
          </p>
        </div>
      </div>
    </div>
  )
}
